import { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtR } from "./orderStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const firstName = (name) => {
  const w = (name || "").trim().split(/\s+/)[0];
  return w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : "there";
};

export const standardMessage = (order, company) => {
  const items = order.items.map((i) => `${i.qty} x ${i.description}`).join(", ");
  return `Hi ${firstName(order.customer_name)}, good news! Your order ${order.ticket_no} (${items}) has arrived at ${company}. Balance due: ${fmtR(order.balance_due)}. Please collect at your convenience.`;
};

export const toWhatsAppNumber = (phone) => {
  let digits = String(phone || "").replace(/\D/g, "");
  if (digits.startsWith("00")) digits = digits.slice(2);
  if (digits.startsWith("0")) digits = `27${digits.slice(1)}`;
  return digits;
};

export const NotifyCustomerDialog = ({ order, onClose, onLogged, by }) => {
  const [company, setCompany] = useState("Bosveld Bou & Hardware");
  const [phone, setPhone] = useState("");
  const [template, setTemplate] = useState("standard");
  const [custom, setCustom] = useState("");

  useEffect(() => {
    if (!order) return;
    setTemplate("standard");
    const lastSent = (order.notifications || []).slice(-1)[0];
    setPhone(order.customer_phone || lastSent?.phone || "");
    axios.get(`${API}/customer-orders/${order.id}/contact`).then((r) => r.data.phone && !lastSent && setPhone(r.data.phone)).catch(() => {});
    axios.get(`${API}/settings/company`).then((r) => r.data.company_name && setCompany(r.data.company_name)).catch(() => {});
  }, [order]);

  useEffect(() => {
    if (order) setCustom(standardMessage(order, company));
  }, [order, company]);

  if (!order) return null;
  const message = template === "standard" ? standardMessage(order, company) : custom;
  const waNumber = toWhatsAppNumber(phone);

  const handleSend = async () => {
    if (waNumber.length < 9) return toast.error("Enter a valid cellphone number");
    if (!message.trim()) return toast.error("Message is empty");
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`, "_blank", "noopener");
    try {
      await axios.post(`${API}/customer-orders/${order.id}/notify`, { channel: "whatsapp", phone, message, template, by });
      onLogged();
    } catch {
      toast.error("WhatsApp opened, but the notification could not be logged");
    }
    onClose();
  };

  const optionCls = (active) => `flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${active ? "border-green-600 bg-green-50 text-green-800" : "hover:bg-gray-50"}`;

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="notify-customer-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageCircle className="h-5 w-5 text-green-600" />Notify customer?</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-600">{order.ticket_no} for <strong>{order.customer_name}</strong> is marked Arrived.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-sm">WhatsApp number</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. 082 123 4567" data-testid="notify-phone-input" />
          </div>
          <div className="flex gap-2">
            <button type="button" className={optionCls(template === "standard")} onClick={() => setTemplate("standard")} data-testid="notify-template-standard">Standard message</button>
            <button type="button" className={optionCls(template === "custom")} onClick={() => setTemplate("custom")} data-testid="notify-template-custom">Write my own</button>
          </div>
          {template === "standard" ? (
            <div className="whitespace-pre-wrap rounded-md border bg-gray-50 p-3 text-sm" data-testid="notify-message-preview">{message}</div>
          ) : (
            <Textarea rows={5} autoCapitalize={false} className="no-capitalize" value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="notify-message-input" />
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="notify-skip-btn">Skip</Button>
          <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={handleSend} data-testid="notify-send-btn">
            <MessageCircle className="h-4 w-4 mr-2" />Send via WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
